import {
  Box,
  Button,
  Checkbox,
  Chip,
  ChipDelete,
  Divider,
  Link,
  Sheet,
  Textarea,
  Typography,
} from "@mui/joy";
import { darken } from "@mui/material";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import SignatureCanvas from "react-signature-canvas";
import { Paper } from "../atoms/paper";
import {
  interactorLabelColor,
  interactorTextOnWhite,
} from "../entities/callDesign";
import { inReplyOrder } from "../entities/replyOrder";
import { uploadInspectorMessages } from "../helpers";
import {
  detectPartialAcceptance,
  findHoldInteractor,
  isAcceptInteractor,
  typedMessage,
} from "../partialAcceptance";
import { PartialAcceptWarning } from "./partialAcceptWarning";
import MissionMap_Entity, {
  Inspector,
  Interactor,
  Message,
} from "../entities/missionMap/missionMap";
import {
  answersSelector,
  inspectorsSelector,
  setBookletJobStageID,
  setInspectors,
  setSelectedBookletToken,
  setSelectedInteractor,
  setSelectedInteractorJobStageID,
  setSelectedToken,
} from "../redux/rhapsodyReducer";
import { useMissionMap } from "../templates/projectInvite";
import { useSearchParams } from "react-router-dom";
import { MissionMap } from "../entities/missionMap";
import { useAnswerMutation } from "../redux/missionMap";
import { useUploadFileMutation } from "../redux/upload";

export function Response({
  seasonMission,
  token,
  overwriteMission,
  jobStageID,
  selectedMessage,
  clearSelectedMessage,
}: {
  seasonMission?: MissionMap;
  selectedMessage?: Message;
  token: string;
  overwriteMission?: MissionMap_Entity;
  clearSelectedMessage?: () => void;
  jobStageID?: number;
}) {
  const [searchParams] = useSearchParams();
  const mainToken = searchParams.get("token") ?? "";
  const _mission = useMissionMap(token);
  const mission = overwriteMission ?? _mission;
  const inspectors = useSelector(inspectorsSelector);
  const dispatch = useDispatch();
  const answers = useSelector(answersSelector);
  const a = answers[token];
  const season = mainToken[0] === "G";
  const [upload] = useUploadFileMutation();
  const [answer] = useAnswerMutation();
  const [loading, setLoading] = useState(false);
  // Set when the musician is about to accept the whole call with a message that
  // reads like they can only play part of it.
  const [partialAccept, setPartialAccept] = useState<Interactor>();

  // Only stages that offer Hold alongside Accept can steer anyone towards it.
  // A season message is not one of them: answerAll() derives every sibling
  // token's answer from its own job stage rather than from the button pressed,
  // so Hold and Accept submit the same thing there and offering the choice
  // would be a false promise.
  const holdInteractor =
    season && selectedMessage
      ? undefined
      : findHoldInteractor(mission?.Interactors);

  // Mirrors ProjectInvite's confirm bar condition. When a bar follows, it owns
  // the partial-acceptance check; these buttons submit immediately and own it.
  const hasConfirmBar = !jobStageID && !season;

  useEffect(() => {
    if (mission?.Interactors.length && !a?.selectedInteractor) {
      dispatch(
        setInspectors(
          mission?.Interactors.reduce<{ [i: number]: Inspector }>((a, v) => {
            a[v.ID] = {
              id: v.ID,
              title: v.Name,
              icon: v.Icon,
              kind: v.Kind,
            };
            return a;
          }, {}),
        ),
      );
    }
  }, []);

  const answerAll = async () => {
    setLoading(true);
    try {
      const body: {
        [token: string]: {
          mercuryInteractorID?: number;
          jobStageID?: number;
          mercuryInspectors?: Inspector[];
        };
      } = {};
      const similar = seasonMission
        ?.allMessages()
        .filter((e) => e.CreatedAt === selectedMessage?.CreatedAt);

      const ins = await uploadInspectorMessages(inspectors, upload);

      similar?.forEach((s) => {
        body[s.token!] = {
          mercuryInteractorID: s?.JobStageID,
          jobStageID: s?.JobStageID,
          mercuryInspectors: ins,
        };
      });

      await answer(body).unwrap();
      if (clearSelectedMessage) clearSelectedMessage();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sends the musician's answer for the given interactor, whichever way the
   * current page answers: season messages answer every sibling token at once,
   * a message opened from the inbox answers its own job stage, and the invite
   * itself hands over to the confirm bar.
   *
   * @param {Interactor} i The interactor the musician chose.
   */
  const submitInteractor = (i: Interactor) => {
    if (season && selectedMessage) {
      answerAll();
      return;
    }
    dispatch(setSelectedInteractor(i));
    if (jobStageID) dispatch(setSelectedInteractorJobStageID(jobStageID));
    if (season && !selectedMessage) dispatch(setSelectedToken(undefined));
  };

  const getInteractor = (i: Interactor) => {
    if (!inspectors[i.ID]) return <Box />;
    const selected = a && a.selectedInteractor?.ID === i.ID;
    switch (i.Kind) {
      case "file":
        return (
          <Upload
            interactor={i}
            onDelete={(f) => {
              const files: File[] = inspectors[i.ID]?.message ?? [];
              dispatch(
                setInspectors({
                  ...inspectors,
                  [i.ID]: {
                    ...inspectors[i.ID],
                    message: files.filter(
                      (e) => !(e.name === f.name && e.size === f.size),
                    ),
                  },
                }),
              );
            }}
            onAdd={(e) => {
              const files: File[] = inspectors[i.ID]?.message ?? [];
              dispatch(
                setInspectors({
                  ...inspectors,
                  [i.ID]: { ...inspectors[i.ID], message: [...files, ...e] },
                }),
              );
            }}
            files={inspectors[i.ID]?.message ?? []}
          />
        );
      case "signature":
        return (
          <Signature
            interactor={i}
            onChange={(s) => {
              dispatch(
                setInspectors({
                  ...inspectors,
                  [i.ID]: { ...inspectors[i.ID], message: s },
                }),
              );
            }}
          />
        );
      case "download":
        return (
          <Button
            fullWidth
            sx={{
              flex: 1,
              // Fill is Mercury's Color, always. The label follows the modern
              // gate: derived for AA on modern calls, historical white/raw on
              // legacy ones (callDesign.ts).
              color:
                selected || !a?.selectedInteractor
                  ? interactorLabelColor(mission, i)
                  : interactorTextOnWhite(mission, i),
              background:
                !selected && a?.selectedInteractor ? "white" : i.Color,
              "&:hover": {
                background: i.Color ? darken(i.Color, 0.1) : undefined,
                color: interactorLabelColor(mission, i),
              },
              "&:active": {
                background: i.Color ? darken(i.Color, 0.2) : undefined,
                color: interactorLabelColor(mission, i),
              },
            }}
            variant={"soft"}
            color="neutral"
            startDecorator={i.Icon ? <i className={i.Icon} /> : undefined}
            onClick={() => {
              window.open(i.Description);
              dispatch(
                setInspectors({
                  ...inspectors,
                  [i.ID]: { ...inspectors[i.ID], message: "Downloaded" },
                }),
              );
            }}
          >
            {i.Name}
          </Button>
        );
      case "checkbox":
        return (
          <Box sx={{ textAlign: "left" }}>
            <Checkbox
              checked={inspectors[i.ID]?.message ?? false}
              onChange={(e) =>
                dispatch(
                  setInspectors({
                    ...inspectors,
                    [i.ID]: { ...inspectors[i.ID], message: e.target.checked },
                  }),
                )
              }
              label={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    level="body-sm"
                    sx={{ color: "black" }}
                    startDecorator={
                      i.Icon ? <i className={i.Icon} /> : undefined
                    }
                  >
                    {i.Name}
                  </Typography>
                  {i.Description ? (
                    <Typography level="body-xs">{i.Description}</Typography>
                  ) : undefined}
                </Box>
              }
            />
          </Box>
        );
      case "text":
        return (
          <Textarea
            value={inspectors[i.ID]?.message ?? ""}
            onChange={(e) =>
              dispatch(
                setInspectors({
                  ...inspectors,
                  [i.ID]: { ...inspectors[i.ID], message: e.target.value },
                }),
              )
            }
            variant="plain"
            placeholder={i.Description}
            startDecorator={
              <Typography
                level="body-sm"
                sx={{ color: "black" }}
                startDecorator={i.Icon ? <i className={i.Icon} /> : undefined}
              >
                {i.Name}
              </Typography>
            }
          />
        );
      case "button":
        return (
          <Button
            loading={loading}
            onClick={() => {
              if (i.KeyName === "onboarding") {
                return window.open(i.Config);
              }
              if (i.Description === "contract") {
                if (_mission?.BookletToken) {
                  dispatch(setSelectedInteractor(i));
                  dispatch(setBookletJobStageID(jobStageID));
                  return dispatch(
                    setSelectedBookletToken(_mission?.BookletToken),
                  );
                }
              }
              // Accepting a whole call while the message explains you can only
              // play part of it tells the contractor two opposite things. Offer
              // Hold before the answer goes out.
              //
              // Only for the paths that submit straight away. Where a confirm
              // bar follows, ProjectInvite guards that instead — the message can
              // still be edited while the bar is up, so the check has to happen
              // at the point of no return rather than here.
              if (!hasConfirmBar && holdInteractor && isAcceptInteractor(i)) {
                if (detectPartialAcceptance(typedMessage(inspectors)).partial)
                  return setPartialAccept(i);
              }
              submitInteractor(i);
            }}
            key={i.Name}
            startDecorator={i.Icon ? <i className={i.Icon} /> : undefined}
            sx={{
              flex: 1,
              // Fill is Mercury's Color, always. The label follows the modern
              // gate: derived for AA on modern calls, historical white/raw on
              // legacy ones (callDesign.ts).
              color:
                selected || !a?.selectedInteractor
                  ? interactorLabelColor(mission, i)
                  : interactorTextOnWhite(mission, i),
              background:
                !selected && a?.selectedInteractor ? "white" : i.Color,
              "&:hover": {
                background: i.Color ? darken(i.Color, 0.1) : undefined,
                color: interactorLabelColor(mission, i),
              },
              "&:active": {
                background: i.Color ? darken(i.Color, 0.2) : undefined,
                color: interactorLabelColor(mission, i),
              },
            }}
          >
            {i.Name}
          </Button>
        );

      default:
        break;
    }
    return (
      <Sheet variant="soft" color="neutral" sx={{ borderRadius: "8px" }}>
        Interactor Not Supported
      </Sheet>
    );
  };

  // Always decline → hold → accept, the same as the season card. The configured
  // Position still orders anything inReplyOrder ranks equally.
  const segueButtons = inReplyOrder(
    (mission?.Interactors ?? [])
      .filter((e) => e.Kind === "button" && e.SegueID)
      .sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0)),
  );
  const otherInteractors = mission?.Interactors?.filter(
    (e) => !(e.Kind === "button" && e.SegueID),
  ).sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0));

  // Modern call: the ask sits flat under the greeting, like the email —
  // buttons first, then the message and other inputs, no card and no heading.
  // Legacy keeps the exact pre-existing "Your response is required" Paper.
  const modern = mission?.ModernCall === true;

  const otherInteractorsBlock = otherInteractors?.length ? (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        flexDirection: "column",
        width: "100%",
      }}
    >
      {otherInteractors?.map((e) => (
        <Sheet
          key={e.ID}
          variant="soft"
          color="neutral"
          sx={{ p: 1, borderRadius: "8px", zIndex: 1300 }}
        >
          {getInteractor(e)}
        </Sheet>
      ))}
    </Box>
  ) : (
    []
  );

  // No .sort() here. This used to re-sort by ID in the middle of a length
  // check — a mutating sort as a side effect of asking "are there any?" — which
  // silently threw away the order decided above.
  const segueButtonsBlock = segueButtons.length ? (
    <Box sx={{ display: "flex", gap: 1, flexDirection: "row", width: "100%" }}>
      {/* Keyed on the interactor, not the array index. getInteractor returns a
          bare element, so this list had no keys at all — React warned about it,
          and reconciling three buttons by position is exactly what must not
          happen now that their order is imposed rather than incidental. */}
      {segueButtons.map((e) => (
        <Fragment key={e.ID}>{getInteractor(e)}</Fragment>
      ))}
    </Box>
  ) : (
    []
  );

  const partialAcceptWarning = (
    <PartialAcceptWarning
      accept={partialAccept}
      hold={holdInteractor}
      companyName={mission?.CompanyName}
      onHold={() => {
        setPartialAccept(undefined);
        if (holdInteractor) submitInteractor(holdInteractor);
      }}
      onAccept={() => {
        const accept = partialAccept;
        setPartialAccept(undefined);
        if (accept) submitInteractor(accept);
      }}
      onEdit={() => setPartialAccept(undefined)}
    />
  );

  if (modern) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          alignItems: "flex-start",
        }}
      >
        {segueButtonsBlock}
        {otherInteractorsBlock}
        {partialAcceptWarning}
      </Box>
    );
  }

  return (
    <Paper>
      <Box>
        <Typography level="title-lg">Your response is required</Typography>
        <Typography level="body-sm">
          Select from the options below your response for
          <br />
          {mission?.CompanyName}
          <i>&quot;{mission?.ProjectName}&quot;</i>
        </Typography>
      </Box>
      {otherInteractorsBlock}
      {segueButtonsBlock}
      {partialAcceptWarning}
    </Paper>
  );
}

export function Upload({
  files,
  onAdd,
  onDelete,
  interactor,
}: {
  files: File[];
  onAdd: (e: File[]) => void;
  onDelete: (e: File) => void;
  interactor: Interactor;
}) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onAdd(acceptedFiles);
    },
    [files],
  );
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
  });

  return (
    <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
      <Box
        {...getRootProps()}
        sx={{
          padding: 1,
          borderRadius: 4,
          background: isDragActive ? "#bbdefb" : "f5f5f5",
        }}
      >
        <input {...getInputProps()} />
        <Typography level="h3">
          <i className="fa-duotone fa-cloud-arrow-up"></i>
        </Typography>
        <Box
          sx={{
            width: "100%",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography
            startDecorator={
              interactor.Icon ? <i className={interactor.Icon} /> : undefined
            }
            level="title-lg"
          >
            {interactor.Name}
          </Typography>
        </Box>
        <Typography level="body-sm">{interactor.Description}.</Typography>
        <Link
          level="body-sm"
          endDecorator={<i className="fa-solid fa-arrow-up-right"></i>}
        >
          Browse Files
        </Link>
        <Typography level="body-xs">100Mb max</Typography>
      </Box>

      {files?.map((f) => (
        <Chip
          sx={{ background: "white" }}
          endDecorator={<ChipDelete onClick={() => onDelete(f)} />}
          key={`${f.name}-${f.size}`}
        >
          {f.name}
        </Chip>
      ))}
    </Box>
  );
}

export function Signature({
  onChange,
  interactor,
}: {
  onChange: (s: any) => void;
  interactor: Interactor;
}) {
  const ref: any = useRef();

  const handleChange = () => {
    const base64 = ref.current.toDataURL();
    const base64Data = base64.split(",")[1];

    // Convert the base64 data to binary data
    const binaryData = atob(base64Data);

    // Create a Uint8Array from the binary data
    const uint8Array = new Uint8Array(binaryData.length);
    for (let i = 0; i < binaryData.length; i++) {
      uint8Array[i] = binaryData.charCodeAt(i);
    }

    // Create a Blob from the Uint8Array
    const blob = new Blob([uint8Array], { type: "image/png" });

    // Create a File object from the Blob
    const file = new File([blob], "image.png", { type: "image/png" });

    onChange(file);
  };

  return (
    <Box sx={{ p: 0.5, textAlign: "left" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
        }}
      >
        <Box>
          <Typography
            startDecorator={
              interactor.Icon ? <i className={interactor.Icon} /> : undefined
            }
            level="body-sm"
            sx={{ color: "black" }}
          >
            {interactor.Name}
          </Typography>
          <Typography level="body-xs">{interactor.Description}</Typography>
        </Box>
        <Chip
          variant="outlined"
          onClick={() => {
            ref.current.clear();
            onChange(undefined);
          }}
          sx={{ background: "white" }}
          size="sm"
        >
          Clear Signature
        </Chip>
      </Box>
      <Divider />
      <Box sx={{ position: "relative", height: 200 }}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            textAlign: "center",
            zIndex: 10,
            width: 500,
            height: 200,
          }}
        >
          <SignatureCanvas
            ref={ref}
            penColor="black"
            onEnd={handleChange}
            canvasProps={{
              width: 500,
              height: 200,
              className: "sigCanvas",
            }}
          />
        </Box>
        <Typography
          level="h1"
          sx={{
            position: "absolute",
            top: 70,
            opacity: 0.1,
            zIndex: 0,
            margin: "auto",
            textAlign: "center",
            width: "100%",
          }}
        >
          Sign Here
        </Typography>
      </Box>
    </Box>
  );
}
